import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import MenuIcon from "@mui/icons-material/MenuRounded";
import {
  AppBar,
  Box,
  IconButton,
  Toolbar,
  Tooltip,
  Typography,
} from "@mui/material";
import { useChatStore } from "@stores/chatStore";
import { useMatchRoute } from "@tanstack/react-router";

interface HeaderProps {
  pageTitle: string;
  onMenuClick: () => void;
  /** Show the chat-history drawer trigger? Only meaningful on chat surfaces. */
  showChatHistoryButton?: boolean;
  onChatHistoryClick?: () => void;
}

/**
 * Mobile-only top bar. Carries the drawer triggers — primary nav on the
 * left, chat history on the right (chat surfaces only) — plus an at-a-
 * glance page or session title since both rails are behind temporary
 * drawers on small screens. Desktop has no top bar.
 */
export function Header({
  pageTitle,
  onMenuClick,
  showChatHistoryButton = false,
  onChatHistoryClick,
}: HeaderProps) {
  const sessionTitle = useChatStore((s) => s.sessionTitle);
  const matchRoute = useMatchRoute();
  const isChatPage =
    !!matchRoute({ to: "/chat" }) ||
    !!matchRoute({ to: "/projects/$projectId/chat" });

  const displayTitle = isChatPage && sessionTitle ? sessionTitle : pageTitle;

  return (
    <AppBar
      position="static"
      sx={{
        bgcolor: "surface.default",
        zIndex: (theme) => theme.zIndex.drawer - 1,
      }}
    >
      <Toolbar sx={{ minHeight: 52, px: 1.5, gap: 1 }}>
        <IconButton
          edge="start"
          onClick={onMenuClick}
          aria-label="Open menu"
          size="small"
          sx={{ mr: 0.5 }}
        >
          <MenuIcon />
        </IconButton>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            flex: 1,
            minWidth: 0,
          }}
        >
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "0.9375rem",
              color: "text.primary",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {displayTitle}
          </Typography>
        </Box>

        {showChatHistoryButton && onChatHistoryClick && (
          <Tooltip title="Chat history">
            <IconButton
              edge="end"
              onClick={onChatHistoryClick}
              aria-label="Open chat history"
              size="small"
            >
              <ForumOutlinedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Toolbar>
    </AppBar>
  );
}
